import { type LeftTop } from "@common/declarations";
import { Common } from "@common/types";
import { arrow, autoUpdate, computePosition, flip, type Middleware, offset, type Placement } from "@floating-ui/dom";

export type ElementLocationObsolete = {
  left: number;
  top: number;
  position: ElementPositionType;
};

const OPPOSITE_POSITIONS: Common.GenericMap<ElementPositionType> = {
  ["top"]: "bottom",
  ["bottom"]: "top",
  ["left"]: "right",
  ["right"]: "left"
};

export type ElementPositionType = "top" | "left" | "bottom" | "right";

export const detectPosition = (
  targetRect: Common.Rect,
  elementRect: Common.Rect,
  preferredPosition: ElementPositionType = "top"
): ElementPositionType => {
  const { height, width } = elementRect;
  const screenRect = document.body.getBoundingClientRect();
  let diffs = [
    { diff: targetRect.top - height, pos: <ElementPositionType>"top" },
    {
      diff: screenRect.height - (targetRect.top + targetRect.height + height),
      pos: <ElementPositionType>"bottom"
    },
    {
      diff: targetRect.left - width - screenRect.left,
      pos: <ElementPositionType>"left"
    },
    {
      diff: screenRect.right - (targetRect.left + targetRect.width + width),
      pos: <ElementPositionType>"right"
    }
  ];

  let position: ElementPositionType;

  // give preference
  if (diffs.find((d) => d.diff > 0 && d.pos === preferredPosition)) {
    position = preferredPosition;
  } else {
    const oppositePreferredPosition = OPPOSITE_POSITIONS[preferredPosition];
    if (diffs.find((d) => d.diff > 0 && d.pos === oppositePreferredPosition)) {
      position = oppositePreferredPosition;
    } else {
      position = diffs.sort((a, b) => b.diff - a.diff)[0].pos;
    }
  }

  if (position === "top") {
    if (screenRect.right - (targetRect.left + targetRect.width / 2 + elementRect.width / 2) < 0) {
      position = "left";
    } else if (targetRect.left + targetRect.width / 2 - elementRect.width / 2 < 0) {
      position = "right";
    }
  }

  return position;
};

export const getElementLocationObsolete = (
  targetRect: Common.Rect,
  elementRect: Common.Rect,
  preferredPosition?: ElementPositionType,
  margin: number = 8
): ElementLocationObsolete => {
  const position = detectPosition(targetRect, elementRect, preferredPosition);
  const screenRect = document.body.getBoundingClientRect();

  let left = 0,
    top = 0;
  switch (position) {
    case "bottom": {
      left = Math.max(
        margin,
        Math.min(
          targetRect.left + targetRect.width / 2 - elementRect.width / 2,
          screenRect.width - elementRect.width - margin
        )
      );
      top = targetRect.bottom + margin;
      break;
    }
    case "left": {
      left = targetRect.left - margin - elementRect.width;
      top = targetRect.top + targetRect.height / 2 - elementRect.height / 2;
      break;
    }
    case "right": {
      left = targetRect.right + margin;
      top = targetRect.top + targetRect.height / 2 - elementRect.height / 2;
      break;
    }
    case "top": {
      left = targetRect.left + targetRect.width / 2 - elementRect.width / 2;
      top = targetRect.top - elementRect.height - margin;
      break;
    }
    default:
      throw new Error(`Unexpected position ${position}`);
  }

  return {
    position,
    left,
    top
  };
};

export type ElementLocation = LeftTop & {
  placement: Placement;
};
export type GetElementLocationOptions = {
  arrowElement?: HTMLElement | null;
  placement?: Placement;
  margin?: number;
};
export const getElementLocation = async (
  targetElement: HTMLElement,
  floatingElement: HTMLElement,
  options?: GetElementLocationOptions
): Promise<ElementLocation> => {
  const middleware: Middleware[] = [flip()];

  if (options?.margin) {
    middleware.push(offset(options.margin));
  }

  if (options?.arrowElement) {
    middleware.push(arrow({ element: options.arrowElement }));
  }

  const res = await computePosition(targetElement, floatingElement, {
    middleware: middleware,
    placement: options?.placement,
    strategy: "absolute"
  });

  if (res.middlewareData.arrow && options?.arrowElement) {
    const { x, y } = res.middlewareData.arrow;

    options.arrowElement.classList.remove("left", "right", "top", "botom");
    options.arrowElement.classList.add(res.placement);

    Object.assign(options.arrowElement.style, {
      transform: positionToTransform(x, y)
    });
  }

  return {
    left: res.x,
    top: res.y,
    placement: res.placement
  };
};

export const trackElementLocation = (
  targetElement: HTMLElement,
  floatingElement: HTMLElement,
  updatePosition: () => void
): (() => void) => autoUpdate(targetElement, floatingElement, updatePosition);

const roundByDpr = (value: number): number => {
  const dpr = window.devicePixelRatio || 1;
  return Math.round(value * dpr) / dpr;
};

export const positionToTransform = (left: number | undefined, top: number | undefined): string =>
  `translate(${left ? roundByDpr(left) : 0}px,${top ? roundByDpr(top) : 0}px)`;
